import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, withClassName } from '@a-type/ui';
import { AnimatePresence, motion, useMotionTemplate, useMotionValue, } from 'motion/react';
import { Children, createContext, memo, useContext, } from 'react';
import { createPortal } from 'react-dom';
import { draggableDataRegistry } from '../dnd/dataRegistry.js';
import { useDndStore } from '../dnd/dndStore.js';
import { gesture } from '../dnd/index.js';
import { useWindowEvent } from '../hooks/useWindowEvent.js';
import cls from './TokenHand.module.css';
import { TokenSpace } from './TokenSpace.js';
import { isToken } from './types.js';
export function TokenHand({ renderDetailed, ref: userRef, className, onDrop, id, children, priority, style, ...rest }) {
    return (_jsxs(TokenHandContext.Provider, { value: true, children: [_jsx(Box, { ref: userRef, full: "width", className: className, style: style, children: _jsx(TokenSpace, { id: id || 'hand', type: "hand", onDrop: (v) => onDrop?.(v), style: {
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 'var(--m-sp-xs)',
                        overflow: 'clip',
                        width: '100%',
                        padding: 'var(--m-sp-xs)',
                    }, priority: priority, ...rest, children: _jsx(AnimatePresence, { children: Children.map(children, (child) => (_jsx(HandItemWrapper, { children: child }))) }) }) }), _jsx(AnimatePresence, { children: renderDetailed && (_jsx(TokenHandPreview, { parentId: id || 'hand', renderDetailed: renderDetailed })) })] }));
}
const TokenHandPreview = memo(function TokenHandPreview({ renderDetailed, parentId, }) {
    // we show a preview when we have a candidate but haven't started dragging yet
    const hasCandidate = useDndStore((state) => !!state.candidate);
    const candidate = useDndStore((state) => state.dragging
        ? null
        : state.candidate
            ? draggableDataRegistry.get(state.candidate)
            : null);
    const previewPosition = useFollowPointer({ x: 0, y: -80 });
    const transform = useMotionTemplate `translate3d(-50%, -100%, 0) translate3d(${previewPosition.x}px, ${previewPosition.y}px, 0)`;
    const overlayEl = useDndStore((state) => state.domOverlayElement);
    if (!candidate || !isToken(candidate)) {
        return null;
    }
    if (gesture.draggedFrom !== parentId) {
        // don't show previews for tokens not in this hand
        return null;
    }
    return createPortal(_jsx(motion.div, { style: {
            transform,
            pointerEvents: 'none',
            userSelect: 'none',
            width: '50vmin',
            height: '50vmin',
            overflow: 'clip',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'absolute',
            zIndex: 10000,
        }, animate: { opacity: 1 }, initial: { opacity: 0 }, exit: { opacity: 0 }, transition: { delay: 0.5, duration: 0.2 }, children: _jsx("div", { style: {
                margin: 'auto',
                maxWidth: '100%',
                maxHeight: '100%',
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'clip',
            }, children: renderDetailed(candidate) }) }), overlayEl || document.body);
});
// causes items to squeeze together if they overflow horizontally
const HandItemWrapper = withClassName('div', cls.itemWrapper);
function useFollowPointer(offset = { x: 0, y: 0 }) {
    const x = useMotionValue(0);
    const y = useMotionValue(0);
    useWindowEvent('pointermove', ({ clientX, clientY }) => {
        // frame.read(() => {
        x.set(clientX + offset.x);
        y.set(clientY + offset.y);
        // });
    });
    return { x, y };
}
const TokenHandContext = createContext(false);
export function useIsTokenInHand() {
    return useContext(TokenHandContext);
}
//# sourceMappingURL=TokenHand.js.map